import { format, isValid, parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createSpecialTask, getTasksByResort } from '../../../../api/endpoints'
import { extractCoordinatesFromGeometry } from '../utils/mapHelpers'
import {
  createTaskDataForSubmission,
  generateTaskId,
  getCurrentDate,
} from '../utils/taskHelpers'
import { validateTaskForm } from '../utils/validations'
import useLocations from '../hooks/useLocations';
import { useAuth } from '../../../../Contexts/AuthContext'; 

const SpecialTaskContext = createContext()

export const SpecialTaskProvider = ({ children }) => {
  const { user, hasModule } = useAuth();
  
  const hasDamageModule = hasModule('damage')

  const [taskId] = useState(generateTaskId())
  const [creationDate] = useState(getCurrentDate())
  const [status] = useState('pending')
  
  const [selectedService, setSelectedService] = useState(() => {
    return user?.service || ''
  })

  useEffect(() => {
     if (user?.service && !selectedService) {
         setSelectedService(user.service);
     }
  }, [user]); 

  const [selectedSlope, setSelectedSlope] = useState(null)
  const [priority, setPriority] = useState('Moyen')
  const [executionDate, setExecutionDate] = useState('')
  const [description, setDescription] = useState('')
  const [photos, setPhotos] = useState([])
  const [markerPosition, setMarkerPosition] = useState(null)
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true)
  const [isRightPanelOpen] = useState(true)
  const [isMapExpanded, setIsMapExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [rawTasksForDemand, setRawTasksForDemand] = useState([])
  const [loadingDemand, setLoadingDemand] = useState(true)
  const [selectedSector, setSelectedSector] = useState('all');
  const [availableSectors, setAvailableSectors] = useState([]);
  const { slopes } = useLocations(); 


  // Fetch tasks for demand calculation
  useEffect(() => {
    // le provider est monté sur toutes les pages : sans le module damage, le site n'a pas de tâches
    if (!hasDamageModule) {
      setRawTasksForDemand([])
      setLoadingDemand(false)
      return
    }

    const fetchDemandData = async () => {
      setLoadingDemand(true)
      try {
        const userInfo = JSON.parse(localStorage.getItem('user'))
        const resortId = userInfo?.user?.resort_id
        if (resortId) {
          const tasks = await getTasksByResort(resortId)
          setRawTasksForDemand(tasks || [])
        } else {
          console.warn('Resort ID not found for demand data fetch.')
          setRawTasksForDemand([])
        }
      } catch (err) {
        console.error('Error fetching tasks for demand data:', err)
        setRawTasksForDemand([])
      } finally {
        setLoadingDemand(false)
      }
    }
    fetchDemandData()
  }, [hasDamageModule])

  useEffect(() => {
    if (slopes && slopes.length > 0) {
      const sectors = [...new Set(slopes.map(s => s.sector).filter(Boolean))].sort();
      setAvailableSectors(sectors);
    }
  }, [slopes]);

  function parseAnyDate(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return null

    let parsed = parseISO(dateStr)
    if (isValid(parsed)) return parsed

    // Handle format dd/MM/yyyy
    const parts = dateStr.split('/')
    if (parts.length === 3) {
      const [day, month, year] = parts
      const fallback = new Date(`${year}-${month}-${day}T00:00:00`)
      return isValid(fallback) ? fallback : null
    }

    return null
  }

  // Calculate demandData
  const demandData = useMemo(() => {
    if (loadingDemand || rawTasksForDemand.length === 0) return {}

    const countByDate = {}
    rawTasksForDemand.forEach(task => {
      if (selectedSector !== 'all' && task.sector !== selectedSector) {
        return;
      }

      const dateStr = task.dateExecution || task.dateDemande
      const parsed = parseAnyDate(dateStr)

      if (parsed) {
        const key = format(parsed, 'yyyy-MM-dd')
        countByDate[key] = (countByDate[key] || 0) + 1
      }
    })

    const mappedColors = {}
    Object.entries(countByDate).forEach(([date, count]) => {
      // Adjust thresholds if needed
      if (count >= 30) mappedColors[date] = 'red'
      else if (count >= 25) mappedColors[date] = 'orange'
      else if (count >= 20) mappedColors[date] = 'green'
      else mappedColors[date] = 'green'
    })
    return mappedColors
  }, [rawTasksForDemand, loadingDemand, selectedSector])

  const handleMapClick = useCallback(coords => {
    setMarkerPosition(coords)
  }, [])

  const handleSlopeSelect = useCallback(location => {
    setSelectedSlope(location)
    if (location) {
      // Check if it's custom (has location_type OR lat/long without geometry)
      const isCustom =
        !!location.location_type ||
        (location.lat != null && location.long != null && !location.geometry)
      const coords = extractCoordinatesFromGeometry(location)
      if (coords) {
        setMarkerPosition(coords)
      } else if (!isCustom) {
        setMarkerPosition(null)
      }
    } else {
      setMarkerPosition(null)
    }
  }, [])

  const handlePhotoUpload = useCallback(files => {
    // files can be a single file or an array of files/FileList
    if (Array.isArray(files)) {
      setPhotos(files);
    } else if (files instanceof FileList) {
      setPhotos(Array.from(files));
    } else {
      setPhotos(prev => [...prev, files]);
    }
  }, [])

  const handleSaveTask = useCallback(async () => {
    const { isValid, errorMessage } = validateTaskForm({
      selectedService,
      selectedSlope,
      executionDate,
      markerPosition,
      description,
    })
    if (!isValid) {
      setError(errorMessage)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const userInfo = JSON.parse(localStorage.getItem('user'))
      const resortId = userInfo?.user?.resort_id
      const userId = userInfo?.user?.id
      if (!resortId || !userId) throw new Error('User/Resort info not found')
      const taskData = createTaskDataForSubmission({
        selectedService,
        selectedSlope,
        executionDate,
        description,
        markerPosition,
        priority,
        photos,
        userId,
        resortId,
      })
      const created = await createSpecialTask(taskData)
      setSuccess(true)
      return created
    } catch (err) {
      console.error('Error saving task:', err)

      if (err.response?.data?.details) {
        const validationErrors = err.response.data.details
        const errorMessages = validationErrors
          .map(error => `${error.field}: ${error.message}`)
          .join(', ')
        setError(`Validation failed: ${errorMessages}`)
      } else if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else {
        setError(err.message || 'Error saving task')
      }
    } finally {
      setLoading(false)
    }
  }, [
    selectedService,
    selectedSlope,
    executionDate,
    description,
    markerPosition,
    priority,
    photos,
  ])

  const resetForm = useCallback(() => {
    setSelectedService(user?.service || '')
    setSelectedSlope(null)
    setPriority('Moyen')
    setExecutionDate('')
    setDescription('')
    setPhotos([])
    setMarkerPosition(null)
    setError(null)
    setSuccess(false)
  }, [])

  const contextValue = {
    taskId,
    creationDate,
    status,
    selectedService,
    setSelectedService,
    selectedSlope,
    setSelectedSlope,
    priority,
    setPriority,
    executionDate,
    setExecutionDate,
    description,
    setDescription,
    photos,
    markerPosition,
    setMarkerPosition,
    isLeftPanelOpen,
    setIsLeftPanelOpen,
    isRightPanelOpen,
    isMapExpanded,
    setIsMapExpanded,
    loading,
    error,
    setError,
    success,
    demandData,
    loadingDemand,
    handleMapClick,
    handleSlopeSelect,
    handlePhotoUpload,
    handleSaveTask,
    resetForm,
    selectedSector,
    setSelectedSector,
    availableSectors,
  }

  return (
    <SpecialTaskContext.Provider value={contextValue}>
      {children}
    </SpecialTaskContext.Provider>
  )
}

SpecialTaskProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useSpecialTask = () => {
  const context = useContext(SpecialTaskContext)
  if (context === undefined)
    throw new Error('useSpecialTask must be used within a SpecialTaskProvider')
  return context
}

export default SpecialTaskContext
